import { useState, useCallback } from 'react';
import { useSetRecoilState } from 'recoil';
import { EmailClientActionLoadingState, useEmailClient } from 'views/email-client/stores';

interface IDeleteAction {
	clientId: string | null;
	clientEmail: string | null;
	modalOpen: boolean;
	loading: boolean;
	usedInComplexFlow: number;
}

export const useDeleteAction = () => {
	const [deleteAction, setDeleteAction] = useState<IDeleteAction>({
		clientId: null,
		clientEmail: null,
		modalOpen: false,
		loading: false,	
		usedInComplexFlow: 0,
	});
	
	const setEmailClientActionLoading = useSetRecoilState(EmailClientActionLoadingState);

	const { removeEmailClient, getSingleEmailClient } = useEmailClient();

	const openDeleteModal = useCallback(async (clientId: string) => {
		setEmailClientActionLoading(prev => ({...prev, [clientId]: true}));
		const resp = await getSingleEmailClient(clientId);
		setEmailClientActionLoading(prev => ({...prev, [clientId]: false}));
		if(resp?.data) {
			setDeleteAction({
				clientId: resp?.data?._id,
				clientEmail: resp?.data?.config?.email,
				modalOpen: true,
				loading: false,
				usedInComplexFlow: resp?.data?.usedInComplexFlow,
			});
		}
	}, [getSingleEmailClient, setEmailClientActionLoading]);


	const closeDeleteModal = useCallback(() => {
		setDeleteAction({
			clientId: null,
			clientEmail: null,
			modalOpen: false,
			loading: false,	
			usedInComplexFlow: 0,
		});
	}, []);

	const handleConfirmDelete = useCallback(async () => {
		if (!deleteAction.clientId) return;

		setDeleteAction(prev => ({ ...prev, loading: true }));
		try {
			await removeEmailClient(deleteAction.clientId);
			closeDeleteModal();
		} catch (error) {
			setDeleteAction(prev => ({ ...prev, loading: false }));
			throw error;
		}
	}, [deleteAction.clientId, removeEmailClient, closeDeleteModal]);

	const handleConfirmationModal = useCallback(async (_isOpen: boolean, confirmed: boolean) => {
		if (confirmed) {
			try {
				await handleConfirmDelete();
			} catch (error) {
				// Handle error silently or show user notification
			}
		} else {
			closeDeleteModal();
		}
	}, [handleConfirmDelete, closeDeleteModal]);

	return {
		deleteAction,
		openDeleteModal,
		handleConfirmationModal,
	};
}; 